import { useEffect, useRef } from "react"
import { UserConsent } from "cookiesConsent/domain/consent"
import { useC15tConsent } from "cookiesConsent/infrastructure/c15t/consent"
import { analyticsActivation } from "cookiesConsent/infrastructure/segment/AnalyticsActivation"
import { SegmentCategoriesFactory } from "cookiesConsent/infrastructure/segment/SegmentCategories"
import { consentBus } from "cookiesConsent/infrastructure/segment/ConsentBus"
import { latestConsent } from "cookiesConsent/features/getConsent/latestConsent"
import { expireTrackersOutsideConsent } from "cookiesConsent/features/expireTrackers"
import { reloadPage } from "cookiesConsent/infrastructure/browser/reloadPage"

const continueWith = (consent: UserConsent): void => {
  analyticsActivation.start(consent)
  latestConsent.publish(consent)
  consentBus.publish(new SegmentCategoriesFactory().fromConsent(consent))
}

const restartWith = (consent: UserConsent): void => {
  latestConsent.publish(consent)
  expireTrackersOutsideConsent(consent)
  reloadPage()
}

const settle = (consent: UserConsent): void => {
  if (analyticsActivation.requiresRestart(consent)) {
    restartWith(consent)
  } else {
    continueWith(consent)
  }
}

const alreadySettled = (
  previous: UserConsent | null,
  next: UserConsent
): boolean => {
  if (!previous) {
    return false
  } else {
    return new SegmentCategoriesFactory()
      .fromConsent(previous)
      .equals(new SegmentCategoriesFactory().fromConsent(next))
  }
}

const awaitDecision = (): void => {}

const keepSettled = (): void => {}

interface AnalyticsLifecycleProps {
  consent: UserConsent
}

const AnalyticsLifecycle = ({ consent }: AnalyticsLifecycleProps) => {
  const { hasConsented } = useC15tConsent()
  const settled = useRef<UserConsent | null>(null)

  useEffect(() => {
    if (!hasConsented) {
      awaitDecision()
    } else if (alreadySettled(settled.current, consent)) {
      keepSettled()
    } else {
      settled.current = consent
      settle(consent)
    }
  }, [hasConsented, consent])

  return null
}

export default AnalyticsLifecycle
